import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "../../store/store";
import { getCompaniesThunk, getProductsByCompanyThunk } from "../../store/thunk/recommendation.thunk";
import Bucket from "./components/Bucket";
import { useNavigate, useSearchParams } from "react-router-dom";

type Product = {
  productId: string;
  displayName: string;
};

type Selected = {
  bucket: "insa" | "general";
  productId: string;
} | null;

type Company = {
  companyId: string;
  companyName: string;
};

const RecommendationEngine: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { companies, products } = useSelector((state: RootState) => state.rootReducer.recommendationSlice);

  const [selectedCompany, setSelectedCompany] = useState<Company | null>(() => {
    const companyName = searchParams.get("companyName");
    if (!companyName) return null;

    const company = companies.find((c) => c.companyName === companyName);
    return company || { companyId: "", companyName: "" };
  });

  const [selected, setSelected] = useState<Selected>(null);

  // Fetch companies on mount
  useEffect(() => {
    dispatch(getCompaniesThunk());
  }, [dispatch]);

  // Fetch products whenever selectedCompany changes
  useEffect(() => {
    if (!selectedCompany) {
      setSelected(null);
      setSearchParams({});
      return;
    }

    if (selectedCompany.companyId) {
      dispatch(getProductsByCompanyThunk(selectedCompany.companyId));
    }

    setSelected(null);
    setSearchParams({
      companyName: selectedCompany.companyName
    });
  }, [selectedCompany, dispatch, setSearchParams]);

  const isDisabled = (productId: string) => selected !== null && selected.productId !== productId;

  const handleSelect = (bucket: "insa" | "general", product: Product) => {
    if (selected && selected.bucket === bucket && selected.productId === product.productId) {
      setSelected(null);
      return;
    }
    setSelected({ bucket, productId: product.productId });
  };

  const handleSubmit = () => {
    if (!selected) return;
    navigate(`/recommendation/benefit-summary/${selected.productId}`);
  };
  

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-semibold mb-6">Choose Your Product</h1>

      {/* Company dropdown */}
      <select
        value={selectedCompany?.companyId || ""}
        onChange={(e) => {
          const company = companies.find((c) => c.companyId === e.target.value);
          setSelectedCompany(company || null);
        }}
        className="mb-8 w-80 border rounded-lg p-2"
      >
        <option value="">Select company</option>
        {companies.map((c) => (
          <option key={c.companyId} value={c.companyId}>
            {c.companyName}
          </option>
        ))}
      </select>

      {selectedCompany && products && (
        <>
          {products.insaRecommendation?.length > 0 && <Bucket title="INSA Recommendations" bucket="insa" products={products.insaRecommendation} selected={selected} onSelect={handleSelect} isDisabled={isDisabled} />}

          {products.general?.length > 0 && <Bucket title="General Recommendations" bucket="general" products={products.general} selected={selected} onSelect={handleSelect} isDisabled={isDisabled} />}

          {/* Show message if both buckets are empty */}
          {products.insaRecommendation?.length === 0 && products.general?.length === 0 && <p className="mt-8 text-center text-gray-500 font-medium">No products found</p>}

          {/* Show Submit button if any products exist */}
          {(products.insaRecommendation?.length > 0 || products.general?.length > 0) && (
            <div className="mt-8 text-center">
              <button disabled={!selected} onClick={handleSubmit} className="px-6 py-3 bg-blue-600 text-white rounded-lg disabled:opacity-40">
                Submit
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RecommendationEngine;
